import { convertDateToYYYYMMDDFormat, IOptions } from '@repo/ui';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface IFilters {
  entity: IOptions;
  course: IOptions;
  semester: IOptions;
  curricularUnit: IOptions;
  group: IOptions;
  period?: Date[];
  starts: string;
  ends: string;
}

export interface IFiltersFormatted {
  entity: string;
  course: string;
  semester: string;
  curricularUnit: string;
  group: string;
  period?: Date[];
  starts: string;
  ends: string;
  hasFilters: boolean;
}

interface FiltersContextProps {
  filters: IFilters | undefined;
  saveFilters: (params: IFilters) => void;
  filtersFormatted: () => Partial<IFiltersFormatted>;
  clearFilters: () => void;
}

const FiltersContext = createContext<FiltersContextProps | undefined>(
  undefined,
);

const FiltersProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<IFilters>();
  const currentDay = new Date();
  const firstDayOfMonth = new Date(
    currentDay.getFullYear(),
    currentDay.getMonth(),
    1,
  );

  const clearFilters = useCallback(() => {
    sessionStorage.removeItem('@filterSaved');
    setFilters({
      starts: convertDateToYYYYMMDDFormat(firstDayOfMonth),
      ends: convertDateToYYYYMMDDFormat(currentDay),
      course: undefined,
      curricularUnit: undefined,
      entity: undefined,
      group: undefined,
      semester: undefined,
    });
  }, []);

  const filtersFormatted = (): Partial<IFiltersFormatted> => {
    if (!filters) return undefined;
    return {
      entity: filters?.entity?.value,
      course: filters?.course?.value,
      semester: filters?.semester?.value,
      curricularUnit: filters?.curricularUnit?.value,
      group: filters?.group?.value,
      period: filters.period
        ? [filters.period[0], filters.period[1]]
        : [firstDayOfMonth, currentDay],
      starts: convertDateToYYYYMMDDFormat(new Date(filters?.starts)),
      ends: convertDateToYYYYMMDDFormat(new Date(filters?.ends)),
      hasFilters: true,
    };
  };

  const saveFilters = useCallback((params: IFilters) => {
    const normalizedParams = {
      ...params,
      starts: convertDateToYYYYMMDDFormat(new Date(params.starts)),
      ends: convertDateToYYYYMMDDFormat(new Date(params.ends)),
      period: [new Date(params.starts), new Date(params.ends)],
    };
    sessionStorage.setItem('@filterSaved', JSON.stringify(normalizedParams));
    setFilters(normalizedParams);
  }, []);

  useEffect(() => {
    const filterSaved = sessionStorage.getItem('@filterSaved');
    if (filterSaved) {
      const { starts, ends, ...rest } = JSON.parse(filterSaved);

      setFilters({
        ...rest,
        starts: new Date(starts).toISOString(),
        ends: new Date(ends).toISOString(),
        period: [new Date(starts), new Date(ends)],
      });
    } else {
      setFilters({
        starts: convertDateToYYYYMMDDFormat(firstDayOfMonth),
        ends: convertDateToYYYYMMDDFormat(currentDay),
        course: undefined,
        curricularUnit: undefined,
        entity: undefined,
        group: undefined,
        semester: undefined,
      });
    }
  }, []);

  return (
    <FiltersContext.Provider
      value={{ filters, saveFilters, filtersFormatted, clearFilters }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

const useFilters = () => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFiltersContext must be used within a FiltersProvider');
  }
  return context;
};

export { FiltersProvider, useFilters };
